import { Injectable } from '@angular/core';
import { asNumber } from '@frontend/unhideschool/app/helpers/utility-functions';
import { ApplicationSelectors } from '@frontend/unhideschool/app/shared-nxgs/application/application.selectors';
import { GetAllProjectsWithSearchText } from '@frontend/unhideschool/app/shared/models/graphql/courses';
import { State, Action, StateContext, Store, Selector } from '@ngxs/store';
import { ProjectPost } from '@frontend/unhideschool/shared/models/project/project-post.model';

import { Observable } from 'rxjs';
import { tap, map } from 'rxjs/operators';

import {
  FetchProjectPostsWithSearchText,
  UpdateProjectpostPagination,
  ResetCourseList,
  GetCoursesValue,
  GetCareerValue,
} from './course-list.action';

import {
  defaultCourseListState,
  CourseListProjectPostStateModel,
  defaultEmptyCourseListPost,
} from './course-list.model';
import { GraphqlApiService } from '@frontend/unhideschool/app/api-gateway/services/graphql-api.service';
import { LoggedUserStateService } from '@frontend/unhideschool/app/core/services/legacy/logged-user.state';
import { getProfilePicture } from '@frontend/unhideschool/app/helpers/relatedmedia-and-crop.helper';
import { RestApiService } from '../../api-gateway/services/rest-api.service';

function mapFetchedCourseListPostToProjectPostWithSearchText(
  promo,
  isAdminUser,
  nextPage = 'view-all'
) {
  return (obs: Observable<any>) => {
    return obs.pipe(
      map((res) => {
        const items: ProjectPost[] = res.data.Courses?.items.map((item) => {
          const projectpost = new ProjectPost(
            item,
            promo,
            nextPage,
            isAdminUser
          );
          projectpost.galleries = item.galleries;
          projectpost.creator.profilepicture = getProfilePicture(
            projectpost.creator.profilepicture,
            projectpost.creator.relatedmedia,
            60
          );
          return projectpost;
        });

        const totalCount: number = res.data?.Courses?.totalCount;
        return { items, totalCount };
      })
    );
  };
}

@State<CourseListProjectPostStateModel>({
  name: 'CourseList',
  defaults: defaultCourseListState,
})
@Injectable()
export class CourseListState {
  constructor(
    private graph: GraphqlApiService,
    private store: Store,
    private luss: LoggedUserStateService,
    private rest: RestApiService
  ) {}

  @Action(FetchProjectPostsWithSearchText)
  fetchProjectPostsWithSearchText(
    ctx: StateContext<CourseListProjectPostStateModel>,
    action: FetchProjectPostsWithSearchText
  ) {
    let state = ctx.getState();
    const filters = action.filters;

    state = {
      ...state,
      ...{
        postLimit: state.postLimit,
        projectposts: state.projectposts,
      },
    };

    if (filters.reset) {
      ctx.patchState(state);
    }

    return this.updateProjectPostStateWithSearchText(ctx, action);
  }

  @Action(ResetCourseList)
  resetCourseList(ctx: StateContext<CourseListProjectPostStateModel>) {
    ctx.patchState({
      notfound: false,
      projectposts: [],
    });
  }

  private prepareLoadProjectItems(
    ctx: StateContext<CourseListProjectPostStateModel>,
    action: FetchProjectPostsWithSearchText,
    projectPosts: ProjectPost[]
  ) {
    const loadingItems = Array.from(
      { length: action.filters.perpage },
      (_, i) => {
        const emptyCourse = defaultEmptyCourseListPost;
        emptyCourse.postid = i + 1;
        return emptyCourse;
      }
    );

    ctx.patchState({
      projectposts: [...projectPosts, ...loadingItems],
      postLimit: false,
    });
  }

  private updateProjectPostStateWithSearchText(
    ctx: StateContext<CourseListProjectPostStateModel>,
    action: FetchProjectPostsWithSearchText
  ) {
    const { page, perpage, nextPage } = action.filters;
    const variables = getRequestVariables(action);

    console.log(variables, variables);

    const stateCourseListProjectPosts: ProjectPost[] =
      ctx.getState().projectposts;

    this.prepareLoadProjectItems(ctx, action, stateCourseListProjectPosts);

    const promo = this.store.selectSnapshot(
      ApplicationSelectors.getActivePromotion()
    );
    return this.graph
      .graphqlRequest(GetAllProjectsWithSearchText, variables)
      .pipe(
        mapFetchedCourseListPostToProjectPostWithSearchText(
          promo,
          this.luss.isAdmin,
          nextPage
        ),
        tap(({ items, totalCount }) => {
          const state = ctx.getState().projectposts;
          let projectposts = [];

          if (page === 1) {
            projectposts = items;
          } else {
            projectposts = [...state, ...items].filter(
              (item) => item.title != 'loading'
            );
          }

          if (projectposts.length > 0) {
            let postLimit = perpage * (page - 1) + items.length >= totalCount;
            if (items.length == 0) {
              postLimit = true;
            }
            ctx.patchState({
              postLimit,
              notfound: false,
              projectposts,
            });
          } else {
            ctx.patchState({
              postLimit: true,
              notfound: ctx.getState().pagination.page == 1,
            });
          }
        })
      );
  }

  @Action(UpdateProjectpostPagination)
  updateProjectpostPagination(
    ctx: StateContext<CourseListProjectPostStateModel>,
    action: UpdateProjectpostPagination
  ) {
    if (action.pagination == undefined) {
      const { page, perpage } = ctx.getState().pagination;

      return ctx.patchState({
        pagination: {
          page: page + 1,
          perpage: perpage,
        },
      });
    }

    const { pagination } = action;
    return ctx.patchState({
      pagination,
    });
  }

  @Action(GetCoursesValue)
  getCoursesValue(
    ctx: StateContext<CourseListProjectPostStateModel>,
    action: GetCoursesValue
  ) {
    return this.rest.get<any>('/post/get_lower_price', {}, true, false).pipe(
      map((result) => result?.dict ?? null),
      tap((dict) => {
        ctx.patchState({ lowerPriceCourseData: dict });
      })
    );
  }

  @Action(GetCareerValue)
  getCareerValue(
    ctx: StateContext<CourseListProjectPostStateModel>,
    action: GetCareerValue
  ) {
    return this.rest.get<any>('/career/get_lower_price', {}, true, false).pipe(
      map((result) => result?.dict ?? null),
      tap((dict) => {
        ctx.patchState({ lowerPriceCareerData: dict });
      })
    );
  }

  @Selector()
  static getLowerCoursePriceData(state: CourseListProjectPostStateModel) {
    return state.lowerPriceCourseData;
  }

  @Selector()
  static getLowerCareerPriceData(state: CourseListProjectPostStateModel) {
    return state.lowerPriceCareerData;
  }
}

function getRequestVariables(action: FetchProjectPostsWithSearchText) {
  const filters = action.filters;

  const variables = {
    orderby: 'RECENT',
    page: filters.page,
    perpage: filters.perpage,
  };

  variables['workspaceIds'] = filters?.workspaceIds?.length ? filters.workspaceIds : [2];

  if (filters.tagid != undefined && filters.tagid.length > 0) {
    variables['tags'] = filters.tagid;
  }

  if (filters.traits != undefined && filters.traits.length > 0) {
    variables['traits'] = filters.traits;
  }

  if (filters.uid != undefined && filters.uid.length > 0) {
    variables['uids'] = filters.uid.map((uid) => asNumber(uid));
  }

  if (filters.search != undefined) {
    variables['search'] = filters.search;
  }

  if (filters.orderby != undefined) {
    variables['orderby'] = filters.orderby;
  }

  if (filters.careerIds != undefined) {
    variables['careerIds'] = filters.careerIds;
  }

  if (Number(filters.postTypeId)) {
    variables['postTypeId'] = filters.postTypeId;
  }

  return variables;
}
